import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
          
          {/* Privacy Policy Section */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">
            This Privacy Policy explains how JobLoop ("we," "us," or "our") collects, uses, and protects your personal information.
          </p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>For Job Seekers:</strong> Name, email address, phone number, professional experience, education, resume, and any other information you voluntarily provide.</li>
              <li><strong>For Employers:</strong> Company name, contact person's name, email address, billing information, and job posting details.</li>
              <li><strong>Automatically Collected Information:</strong> We may collect data about your use of the Website, such as your IP address, browser type, device information, and pages you visit.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-3">We use the collected information for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>To provide and improve our Services.</li>
              <li>To match Job Seekers with relevant job opportunities.</li>
              <li>To facilitate communication between Employers and Job Seekers.</li>
              <li>To process payments for our services.</li>
              <li>To personalize your experience on the Website.</li>
              <li>To analyze user behavior and enhance our platform's functionality.</li>
              <li>To send you service-related notifications, updates, and promotional content (with your consent).</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing</h2>
            <ul className="list-disc list-inside space-y-3 text-gray-700">
              <li><strong>With Employers and Recruiters:</strong> When you apply for a job or create a profile on JobLoop, your contact information (including phone number and email address), resume, and application data will be shared with employers and recruiters. By creating a profile or applying for jobs, you consent to this sharing of information for recruitment purposes.</li>
              <li><strong>With Third-Party Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf, such as payment processing, data analysis, and email delivery. These providers are obligated to protect your information and use it only for the purposes for which it was disclosed.</li>
              <li><strong>Legal Compliance:</strong> We may disclose your information if required by law, court order, or government regulation.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
            <p className="text-gray-700">
              We implement reasonable security measures to protect your personal information from unauthorized access, use, or disclosure. However, no method of data transmission over the internet is 100% secure.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Your Choices</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>You can access and update your account information at any time.</li>
              <li>You can unsubscribe from marketing emails by clicking the "unsubscribe" link.</li>
              <li>You can request the deletion of your account and personal data by contacting us at admin@jobloop.in.</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="my-12 border-t-2 border-gray-200"></div>

          {/* Refund & Cancellation Policy Section */}
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund & Cancellation Policy</h1>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. For Employers (Subscription Services)</h2>
            
            <div className="mb-4 bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="text-gray-800">
                <strong>Note:</strong> Subscription fees for access to candidate contact information and other premium features are non-refundable once the service period has commenced.
              </p>
            </div>
            
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Cancellation</h3>
              <p className="text-gray-700">
                You may cancel a job posting at any time. However, cancellation of a live job posting does not entitle you to a refund.
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Refund Eligibility</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><strong>Full Refund:</strong> A full refund may be issued only if the job posting was not activated or published on the Website for any reason (technical error, failure to meet our content guidelines before publication).</li>
                <li><strong>Partial Refund:</strong> We do not offer partial refunds for job posts that have been active for any duration, regardless of the number of applications received.</li>
                <li><strong>Non-Refundable Services:</strong> All paid services, including but not limited to, featured listings, resume database access, and job bundles, are non-refundable once the service has commenced.</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Disputes</h3>
              <p className="text-gray-700">
                If a payment dispute arises due to a technical error on our part, we will investigate and, if the error is confirmed, a refund will be processed.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Refund Process</h2>
            <p className="text-gray-700 mb-3">
              To request a refund, please contact us at admin@jobloop.in with the following information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Your name and company name.</li>
              <li>The transaction ID or invoice number.</li>
              <li>The reason for the refund request.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Refund requests will be reviewed on a case-by-case basis. If approved, the refund will be processed & credited within 5 to 10 business days to the original payment method.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Changes to the Policy</h2>
            <p className="text-gray-700">
              We reserve the right to modify this Refund & Cancellation Policy at any time. Changes will be effective immediately upon posting on the Website. Your continued use of our services after such changes constitutes your acceptance of the new policy.
            </p>
          </section>

          {/* Last Updated */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Last Updated: October 29, 2025
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 JobLoop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
